// SPDX-License-Identifier: GPL-3.0-only
// Copyright (C) 2026 Kyle Johnson
// This file is part of Hushold.
//
// Hushold is free software: you can redistribute it and/or modify
// it under the terms of the GNU General Public License as
// published by the Free Software Foundation, version 3.

import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useApp } from '../../store/AppContext.js';
import { useOnlineStatus } from '../../native/useNative.js';
import { BottomSheet } from './BottomSheet.js';
import { ChevronLeftIcon } from './icons.js';

interface MobileTopBarProps {
  title: string;
  // When true, show a back chevron that pops the navigation stack.
  back?: boolean;
  // Optional right-aligned action (e.g. a "Save" or "New" button).
  right?: React.ReactNode;
  // Show the condensed sync/offline status dot (default true).
  showStatus?: boolean;
}

// DECISION (Phase 3): a minimal 48px top bar. The desktop's verbose sync label
// ("Synced" / "Sync error") is condensed to a single tappable status dot to save
// horizontal space; tapping opens a sheet with the full detail and a manual sync.
// Height + safe-area top padding keep the title clear of the notch on iOS.
export function MobileTopBar({ title, back = false, right, showStatus = true }: MobileTopBarProps) {
  const { t } = useTranslation(['common', 'mobile', 'errors']);
  const navigate = useNavigate();
  return (
    <header className="shrink-0 bg-bg-surface border-b border-border pt-safe no-print">
      <div className="h-12 flex items-center px-2 gap-1">
        {back ? (
          <button
            onClick={() => navigate(-1)}
            aria-label={t('actions.back')}
            className="w-10 h-10 flex items-center justify-center text-text-muted active:text-text -ml-1"
          >
            <ChevronLeftIcon size={22} />
          </button>
        ) : (
          <span className="w-2" />
        )}
        <h1 className="flex-1 text-md font-semibold text-text truncate">{title}</h1>
        {showStatus && <SyncStatusDot />}
        {right && <div className="ml-1 flex items-center">{right}</div>}
      </div>
    </header>
  );
}

// Condensed sync/offline indicator: a small dot, tap for detail.
function SyncStatusDot() {
  const { t } = useTranslation(['common', 'mobile', 'errors']);
  const { state } = useApp();
  const { sync } = state;
  const online = useOnlineStatus();
  const [open, setOpen] = useState(false);

  // Offline takes visual precedence — a muted amber dot (non-alarming, per Phase 10).
  const color = !online
    ? 'bg-warning'
    : {
        idle: 'bg-text-subtle',
        syncing: 'bg-accent',
        success: 'bg-positive',
        error: 'bg-negative',
      }[sync.status];

  const label = !online
    ? t('sync.offline')
    : sync.status === 'syncing'
      ? t('sync.syncing')
      : sync.status === 'error'
        ? t('sync.error')
        : sync.status === 'success'
          ? t('sync.synced')
          : t('sync.idle');

  return (
    <>
      <button
        onClick={() => setOpen(true)}
        aria-label={t('sync.syncStatusLabel', { label })}
        className="w-10 h-10 flex items-center justify-center"
      >
        <span className={`w-2.5 h-2.5 ${color} ${sync.status === 'syncing' && online ? 'animate-pulse' : ''}`} />
      </button>

      <BottomSheet open={open} onClose={() => setOpen(false)} title={t('sync.syncStatus')}>
        <div className="px-4 py-3 space-y-2 text-md">
          <div className="flex justify-between">
            <span className="text-text-muted">{t('sync.connectivity')}</span>
            <span className={online ? 'text-positive' : 'text-warning'}>
              {online ? t('sync.online') : t('sync.offline')}
            </span>
          </div>
          <div className="flex justify-between">
            <span className="text-text-muted">{t('sync.status')}</span>
            <span className="text-text">{label}</span>
          </div>
          <div className="flex justify-between">
            <span className="text-text-muted">{t('sync.lastSynced')}</span>
            <span className="text-text font-mono text-sm">
              {sync.lastSyncAt ? new Date(sync.lastSyncAt).toLocaleString() : '—'}
            </span>
          </div>
          {sync.errorMessage && (
            <p className="text-sm text-negative pt-1">{sync.errorMessage}</p>
          )}
          {!online && (
            <p className="text-sm text-text-muted pt-2">
              {t('sync.offlineNote')}
            </p>
          )}
        </div>
      </BottomSheet>
    </>
  );
}
